import React, { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROLE_UPDATED_SUCCESSFULLY } from '@/utils/constants/messages';
import StackButton from "@/components/buttons/StackButton";
import { FormPageNameField } from "@/components/forms/FormPageNameField";
import StatusToggle from "@/components/toggles/StatusToggle";
import { FormToggleListPageSkeleton } from "@/components/skeletons";
import { getRoleById, updateRole } from "@/services/role.service";
import { getFeaturesListFromAPI } from '@/services/setting.service';
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_FEATURES } from '@/utils/constants/constants';
import { storeInLocalStorage } from '@/utils/functions/helperFunctions';
import { errorToasterMessage, successToasterMessage } from '@/utils/functions/toaster';

const fetchRoleById = (id: string) =>
    new Promise<any>((resolve, reject) => {
        getRoleById(id, resolve, reject);
    });

const EditRole = () => {
    const params = useParams();
    const navigate = useNavigate();
    const role_id = params.id || "";
    const [roleDetails, setRoleDetails] = useState<any>({});
    const [allFeatures, setAllFeatures] = useState<any>([]);
    const [checkedCount, setCheckedCount] = useState(0);
    const [loader, setLoader] = useState(false);
    const [pageReady, setPageReady] = useState(false);

    useLayoutEffect(() => {
        setPageReady(false);
        setRoleDetails({});
        setAllFeatures([]);
        setCheckedCount(0);
    }, [role_id]);

    useEffect(() => {
        let cancelled = false;

        const loadPage = async () => {
            try {
                const roleResponse = await fetchRoleById(role_id);
                if (cancelled) {
                    return;
                }

                const featuresResponse = await getFeaturesListFromAPI();
                if (cancelled) {
                    return;
                }

                const role = roleResponse.data;
                storeInLocalStorage(ALL_FEATURES, featuresResponse.data.features);

                let checkedCountLocal = 0;
                const newFeaturesList = featuresResponse.data.features.map((feature: any) => {
                    const checked = role?.features?.some((name: string) => name === feature.name) ?? false;
                    checkedCountLocal += checked ? 1 : 0;
                    return {
                        name: feature.name,
                        checked,
                    };
                });

                setRoleDetails(role);
                setCheckedCount(checkedCountLocal);
                setAllFeatures(newFeaturesList);
                setPageReady(true);
            } catch (error) {
                if (!cancelled) {
                    console.log(error);
                    setPageReady(true);
                }
            }
        };

        loadPage();

        return () => {
            cancelled = true;
        };
    }, [role_id]);

    const onChangeFeature = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFeatureList = allFeatures.map((feature: any) => {
            if (e.target.name === feature.name) {
                const new_feature = { ...feature, checked: e.target.checked };
                setCheckedCount(checkedCount + (e.target.checked ? 1 : -1));
                return new_feature;
            }
            return feature;
        });
        setAllFeatures(newFeatureList);
    };

    const handleEditRole = () => {
        const newFeatureList = allFeatures.filter((feature: any) => feature.checked);
        const newFeatureNameList = newFeatureList.map((feature: any) => feature.name);
        const roleUpdateData = {
            features: newFeatureNameList,
        };
        setLoader(true);
        updateRole(role_id, JSON.stringify(roleUpdateData), () => {
            setLoader(false);
            successToasterMessage(ROLE_UPDATED_SUCCESSFULLY);
            navigate(-1);
        }, (error: any) => {
            setLoader(false);
            errorToasterMessage(error.response.data.message);
        });
    };

    if (!pageReady) {
        return <FormToggleListPageSkeleton sectionTitle={HEADINGS.PERMISSIONS} />;
    }

    const isSaveDisabled = checkedCount === 0 || !roleDetails.name?.trim();

    return (
        <div className="gs-form-page">
            <div className="gs-form-page-header">
                <h3 className="gs-form-page-title">
                    <strong id="edit-role-name">{roleDetails.name}</strong>
                </h3>
                <StackButton
                    secondaryVariant="light"
                    primaryId="edit-role-save-btn"
                    secondaryId="edit-role-cancel-btn"
                    primaryButton={BUTTONS.SAVE}
                    secondaryButton={BUTTONS.CANCEL}
                    handlePrimary={handleEditRole}
                    handleSecondary={() => navigate(-1)}
                    disabled={isSaveDisabled}
                    isLoading={loader}
                />
            </div>

            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    if (!isSaveDisabled && !loader) {
                        handleEditRole();
                    }
                }}
            >
                <FormPageNameField
                    label={LABELS.ROLE_NAME}
                    labelId="edit-role-name-label"
                    inputId="edit-role-name-input"
                    value={roleDetails.name || ""}
                    disabled
                    readOnly
                />

                <div className="gs-form-page-section">
                    <h5 className="gs-form-section-title gs-form-section-title--spaced" id="edit-role-permission-title">
                        {HEADINGS.PERMISSIONS}
                    </h5>
                    <div className="gs-list-page-card">
                        <ul className="gs-detail-list gs-form-toggle-list">
                            {allFeatures.map((feature: any, key: number) => (
                                <li className="gs-detail-list-item" key={feature.name}>
                                    <StatusToggle
                                        index={key}
                                        onChange={onChangeFeature}
                                        name={feature.name as string}
                                        id={feature.name as string}
                                        checked={feature.checked}
                                    >
                                        <p className="m-0" id={`edit-role-permission-feature-${key}`}>{feature.name}</p>
                                    </StatusToggle>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditRole;
